import { inject, Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  NgxsOnInit,
  Selector,
  State,
  StateContext
} from '@ngxs/store';
import {
  ClearRestaurantAction,
  GetPickupLocationsAction,
  GetRestaurantGeneralAction,
  GetRestaurantPaymentMethodsAction,
  GetRestaurantProductCategoriesAction,
  GetRestaurantProductsAction
} from './restaurant.actions';
import { WooCommerceProduct } from '../../../core/interfaces/woo-commerce/api/products/product-api.interface';
import { WooCommerceProductCategory } from '../../../core/interfaces/woo-commerce/api/products/product-category-api.interface';
import {
  WooCommerceRestaurant,
  WooCommerceRestaurantBanner,
  WooCommerceRestaurantHolidays,
  WooCommerceRestaurantHoursValue
} from '../../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import { isTimeInRange } from '../../utils/time.util';
import { convertToString, getWeekdayName } from '../../utils/date.utils';
import {
  isTodayHoliday,
  isDateHoliday,
  addDays
} from '../../utils/holidays.utils';
import { finalize, map, Observable, tap } from 'rxjs';
import { WooCommerceProductApiService } from '../../../core/services/woo-commerce/api/woo-commerce-product-api.service';
import { array2Object } from '../../utils/convert-types.utils';
import { mergeObjects } from '../../utils/deep-merge.utils';
import {
  WooCommerceBaseProductAddon,
  WooCommerceBaseProductCategory
} from '../../../core/interfaces/woo-commerce/api/products/base-product-api.interface';
import {
  WooCommerceDatetimeDeliveryDate,
  WooCommerceShippingAddress
} from '../../../core/interfaces/woo-commerce/api/woo-commerce-api.interface';
import { WooCommercePaymentMethod } from '../../../core/interfaces/woo-commerce/api/payments/payments-api.interface';
import { WooCommercePaymentApiService } from '../../../core/services/woo-commerce/api/woo-commerce-payment-api.service';
import { WooCommerceRestaurantApiService } from '../../../core/services/woo-commerce/api/woo-commerce-restaurant-api.service';
import { WooCommerceShippingApiService } from '../../../core/services/woo-commerce/api/woo-commerce-shipping-api.service';
import { BackendThemeService } from '../../../core/services/backend-theme.service';
import { CustomThemeService } from '../../../core/services/custom-theme.service';
import { HappyStoreInfoCacheService } from '../../../core/services/happy/happy-store-info-cache.service';
import { HappyStoreSelectionService } from '../../../core/services/happy/happy-store-selection.service';
import { CacheVersionsReconcileService } from '../../../core/services/cache-versions/cache-versions-reconcile.service';
import { environment } from '../../../../environments/environment';
import { updateFavicon } from '../../utils/favicon.utils';
import { getPaymentIconByTitle } from '../../utils/payment.util';

export interface RestaurantGeneralLeadTime {
  pick_up_lead_time: string;
  delivery_lead_time: string;
}

export interface RestaurantGeneralDeliveryTypes {
  delivery_enabled: boolean;
  pickup_enabled: boolean;
}

export interface RestaurantGeneralIcons {
  main_icon: string;
}

export interface RestaurantGeneralState
  extends RestaurantGeneralLeadTime,
    RestaurantGeneralDeliveryTypes,
    RestaurantGeneralIcons {
  name: string;
  description: string;
  aboutUs: string;
  phone: string;
  email: string;
  address: string;
  genre?: string;
  storeType?: string;
  logo?: string;
  hero?: string;
  timezone?: string;
}

export interface WooCommerceProductCategoryObject {
  [key: number]: WooCommerceProductCategory;
}

export interface WooCommerceProductsObject {
  [key: number]: WooCommerceProduct[];
}

type RestaurantStateLoadingKey =
  | 'general'
  | 'products'
  | 'categories'
  | 'payments';
type RestaurantStateLoading = {
  [key in RestaurantStateLoadingKey]: boolean;
};

export interface RestaurantStateModel {
  general: RestaurantGeneralState;
  banners: WooCommerceRestaurantBanner[];
  workingHours: WooCommerceRestaurantHoursValue[];
  holidays: WooCommerceRestaurantHolidays;
  pickupLocations: WooCommerceShippingAddress[];
  productCategories: WooCommerceProductCategoryObject;
  productCategoriesOrder: number[];
  paymentMethods: WooCommercePaymentMethod[];
  products: WooCommerceProductsObject;
  loading: RestaurantStateLoading;
}

const defaultGeneral: RestaurantGeneralState = {
  name: '',
  description: '',
  aboutUs: '',
  phone: '',
  email: '',
  address: '',
  logo: '',
  hero: '',
  main_icon: '',
  pick_up_lead_time: '',
  delivery_lead_time: '',
  delivery_enabled: true,
  pickup_enabled: true
};

const defaults: RestaurantStateModel = {
  general: defaultGeneral,
  banners: [],
  workingHours: [],
  holidays: {},
  productCategories: {},
  productCategoriesOrder: [],
  paymentMethods: [],
  products: {},
  pickupLocations: [],
  loading: {
    general: false,
    products: false,
    categories: false,
    payments: false
  }
};

export const mapProducts = (
  products: WooCommerceProduct[]
): WooCommerceProduct[] => {
  return products.reduce(
    (acc: WooCommerceProduct[], rawProduct: WooCommerceProduct) => {
      const productAddons: WooCommerceBaseProductAddon[] = Array.isArray(
        rawProduct.product_addons
      )
        ? rawProduct.product_addons
        : Object.values(rawProduct.product_addons);

      const product: WooCommerceProduct = {
        ...rawProduct,
        images: rawProduct.images?.map((image) => ({
          ...image,
          src: image.src?.includes('placeholder') ? '' : image.src,
          placeholder: image.src?.includes('placeholder') ? image.src : ''
        })),
        product_addons: productAddons?.map(
          (productAddon, productAddonIndex) => ({
            ...productAddon,
            key: `${productAddon.id || productAddonIndex}`,
            options: productAddon?.options?.map((option, index) => ({
              ...option,
              value: index
            }))
          })
        )
      };

      acc.push(product);
      return acc;
    },
    []
  );
};

export const groupProductsByCategory = (
  products: WooCommerceProduct[]
): WooCommerceProductsObject => {
  return products.reduce((acc, product: WooCommerceProduct) => {
    product.categories.forEach((category: WooCommerceBaseProductCategory) => {
      if (!acc[category.id]) {
        acc[category.id] = [];
      }

      const isProductInArray = acc[category.id].find(
        (categoryProduct) => categoryProduct.id === product.id
      );

      if (isProductInArray) {
        return;
      }

      acc[category.id].push(product);
    });

    return acc;
  }, {} as WooCommerceProductsObject);
};

@State<RestaurantStateModel>({
  name: 'restaurant',
  defaults
})
@Injectable()
export class RestaurantState implements NgxsOnInit {
  private readonly customThemeService = inject(CustomThemeService);
  private readonly cacheVersionsReconcile = inject(
    CacheVersionsReconcileService
  );

  protected get activeLogo(): string {
    return (
      this.customThemeService.getManifestRestaurant()?.logo ||
      environment.restaurant.logo ||
      ''
    );
  }

  protected get activeFavicon(): string {
    return (
      this.customThemeService.getManifestRestaurant()?.favicon ||
      ((environment as any)?.restaurant as any)?.['favicon'] ||
      ''
    );
  }

  constructor(
    private wooCommerceProductApiService: WooCommerceProductApiService,
    private wooCommerceRestaurantApiService: WooCommerceRestaurantApiService,
    private wooCommercePaymentApiService: WooCommercePaymentApiService,
    private wooCommerceShippingApiService: WooCommerceShippingApiService,
    private themeConfigService: BackendThemeService,
    private happyStoreInfoCache: HappyStoreInfoCacheService,
    private happyStoreSelection: HappyStoreSelectionService
  ) {}

  /**
   * Seed restaurant state from the last cached `/store/info` response for
   * the currently-selected store (keyed by `x-store-api-key`). This avoids
   * a visible flash of an empty header (logo, name, hours) while the
   * network request is in flight — especially when switching locations,
   * where most of the time the identity doesn't change between stores.
   *
   * The actual HTTP response, when it arrives, goes through the same
   * reducer path and overwrites the seeded data.
   */
  ngxsOnInit(ctx: StateContext<RestaurantStateModel>): void {
    const cached = this.readCachedStoreInfo();
    if (!cached) {
      return;
    }

    this.applyRestaurantPayload(ctx, cached);
  }

  static isLoading(key: RestaurantStateLoadingKey) {
    return createSelector([RestaurantState], (state: RestaurantStateModel) => {
      return state?.loading?.[key];
    });
  }

  static shippingMethod(pickupId: string) {
    return createSelector([RestaurantState], (state: RestaurantStateModel) => {
      const pickupOrderLocation: WooCommerceShippingAddress =
        state.pickupLocations.find(({ id }) => pickupId === id)!;

      return pickupOrderLocation || null;
    });
  }

  static scheduleTimeSlots(pickupId: string, date: Date) {
    return createSelector([RestaurantState], (state: RestaurantStateModel) => {
      const pickupOrderLocation: WooCommerceShippingAddress =
        state.pickupLocations.find(({ id }) => pickupId === id)!;

      if (!pickupOrderLocation || !pickupOrderLocation?.datetime_settings) {
        return [];
      }

      return (
        pickupOrderLocation?.datetime_settings?.delivery_dates?.find(
          (deliveryDate: WooCommerceDatetimeDeliveryDate): boolean => {
            return deliveryDate?.date === convertToString(date);
          }
        )?.time_slots || []
      );
    });
  }

  @Selector()
  static paymentMethods(
    state: RestaurantStateModel
  ): WooCommercePaymentMethod[] {
    return state.paymentMethods;
  }

  @Selector()
  static products(state: RestaurantStateModel): WooCommerceProductsObject {
    return state.products;
  }

  @Selector()
  static general(state: RestaurantStateModel): RestaurantGeneralState {
    return state?.general;
  }

  @Selector()
  static banners(state: RestaurantStateModel): WooCommerceRestaurantBanner[] {
    return state?.banners;
  }

  @Selector()
  static productCategories(
    state: RestaurantStateModel
  ): WooCommerceProductCategoryObject {
    return state?.productCategories;
  }

  @Selector()
  static productCategoriesOrder(state: RestaurantStateModel): number[] {
    return state?.productCategoriesOrder || [];
  }

  @Selector()
  static isOpen(state: RestaurantStateModel): boolean {
    const timezone = state?.general?.timezone;
    const currentDate = new Date();

    // Check if today is a holiday - if so, restaurant is closed
    if (isTodayHoliday(state?.holidays, timezone)) {
      return false;
    }

    const currentWeekday: string = getWeekdayName(currentDate, timezone);
    const todaySchedule = state?.workingHours.find(
      (day: WooCommerceRestaurantHoursValue) => day.weekday === currentWeekday
    );

    if (!todaySchedule || !todaySchedule.is_opened) {
      return false;
    }

    return isTimeInRange(
      todaySchedule?.start_time,
      todaySchedule?.close_time,
      currentDate,
      timezone
    );
  }

  @Selector()
  static isTodayHoliday(state: RestaurantStateModel): boolean {
    const timezone = state?.general?.timezone;
    return isTodayHoliday(state?.holidays, timezone);
  }

  @Selector()
  static holidays(state: RestaurantStateModel): WooCommerceRestaurantHolidays {
    return state?.holidays || {};
  }

  @Selector()
  static todayWorkingHours(
    state: RestaurantStateModel
  ): WooCommerceRestaurantHoursValue | undefined {
    const timezone = state?.general?.timezone;
    const currentDate = new Date();
    const currentWeekday: string = getWeekdayName(currentDate, timezone);
    return state?.workingHours.find(
      (day: WooCommerceRestaurantHoursValue) => day.weekday === currentWeekday
    );
  }

  @Selector()
  static nextOpenDay(
    state: RestaurantStateModel
  ): WooCommerceRestaurantHoursValue | undefined {
    const timezone = state?.general?.timezone;
    const currentDate = new Date();
    const workingHours = state?.workingHours || [];
    const holidays = state?.holidays || {};

    // Check up to 14 days ahead to find the next open day (accounting for holidays)
    for (let i = 1; i <= 14; i++) {
      const nextDate = addDays(currentDate, i);

      // Skip if this date is a holiday
      if (isDateHoliday(nextDate, holidays, timezone)) {
        continue;
      }

      const nextWeekday = getWeekdayName(nextDate, timezone);
      const nextDaySchedule = workingHours.find(
        (day: WooCommerceRestaurantHoursValue) => day.weekday === nextWeekday
      );

      if (
        nextDaySchedule &&
        nextDaySchedule.is_opened &&
        nextDaySchedule.start_time
      ) {
        return nextDaySchedule;
      }
    }

    return undefined;
  }

  /**
   * Returns the actual next open date (Date object) and the schedule info
   * This is useful for showing "Opens Friday at 7:00 AM" when there are multiple holidays
   */
  @Selector()
  static nextOpenDateInfo(
    state: RestaurantStateModel
  ): { date: Date; schedule: WooCommerceRestaurantHoursValue } | undefined {
    const timezone = state?.general?.timezone;
    const currentDate = new Date();
    const workingHours = state?.workingHours || [];
    const holidays = state?.holidays || {};

    // Check up to 14 days ahead to find the next open day (accounting for holidays)
    for (let i = 1; i <= 14; i++) {
      const nextDate = addDays(currentDate, i);

      // Skip if this date is a holiday
      if (isDateHoliday(nextDate, holidays, timezone)) {
        continue;
      }

      const nextWeekday = getWeekdayName(nextDate, timezone);
      const nextDaySchedule = workingHours.find(
        (day: WooCommerceRestaurantHoursValue) => day.weekday === nextWeekday
      );

      if (
        nextDaySchedule &&
        nextDaySchedule.is_opened &&
        nextDaySchedule.start_time
      ) {
        return { date: nextDate, schedule: nextDaySchedule };
      }
    }

    return undefined;
  }

  @Selector()
  static workingHours(
    state: RestaurantStateModel
  ): WooCommerceRestaurantHoursValue[] {
    return state?.workingHours;
  }

  @Selector()
  static pickupLocations(
    state: RestaurantStateModel
  ): WooCommerceShippingAddress[] {
    return Object.values(state.pickupLocations);
  }

  @Selector()
  static leadTimes(state: RestaurantStateModel): RestaurantGeneralLeadTime {
    return {
      pick_up_lead_time: state.general.pick_up_lead_time,
      delivery_lead_time: state.general.delivery_lead_time
    };
  }

  @Selector()
  static deliveryTypes(
    state: RestaurantStateModel
  ): RestaurantGeneralDeliveryTypes {
    return {
      delivery_enabled: state.general.delivery_enabled,
      pickup_enabled: state.general.pickup_enabled
    };
  }

  @Selector()
  static timezone(state: RestaurantStateModel): string | undefined {
    return state.general?.timezone;
  }

  @Selector()
  static storeType(state: RestaurantStateModel): string | undefined {
    return state.general?.storeType;
  }

  // Get
  @Action(GetRestaurantGeneralAction)
  getRestaurantGeneral(
    ctx: StateContext<RestaurantStateModel>
  ): Observable<WooCommerceRestaurant> {
    const loading: RestaurantStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, general: true } });

    return this.wooCommerceRestaurantApiService.getRestaurantInfo().pipe(
      tap((payload: WooCommerceRestaurant) => {
        this.applyRestaurantPayload(ctx, payload);
      }),
      finalize(() => {
        const updatedLoading: RestaurantStateLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, general: false } });
      })
    );
  }

  /**
   * Builds the restaurant state slice from a `/store/info` payload and
   * patches it into the store. Also applies theme + favicon side effects.
   * Used by both the HTTP action handler and the cache-based ngxsOnInit
   * hydrator so both paths stay in sync.
   */
  private applyRestaurantPayload(
    ctx: StateContext<RestaurantStateModel>,
    payload: WooCommerceRestaurant
  ): void {
    const addressParts: string[] = [
      payload.store?.address_1,
      payload.store?.address_2,
      payload.store?.city || '',
      payload.store?.state || '',
      payload.store?.postcode,
      payload.store?.country
    ].filter(Boolean) as string[];
    const fullAddress: string = addressParts.join(', ');

    const formatLeadTime = (value: string | number | undefined): string => {
      const minutes = Number(value);
      if (!Number.isFinite(minutes) || minutes <= 0) {
        return '';
      }
      return `${minutes} minutes`;
    };

    const general: RestaurantGeneralState = {
      // In multi-location setup, backend payload contains the currently selected
      // store name, while environment.restaurant.name is static per build.
      // Prefer payload so switching location updates UI immediately.
      name: payload.name || environment.restaurant?.name,
      description: payload['description '],
      aboutUs: payload.about_us || payload['description '] || '',
      email: payload.location?.email?.value || '',
      address: fullAddress,
      pick_up_lead_time: formatLeadTime(payload.location?.pick_up_lead_time),
      delivery_lead_time: formatLeadTime(payload.location?.delivery_lead_time),
      delivery_enabled: payload.location?.delivery_enabled ?? true,
      pickup_enabled: payload.location?.pickup_enabled ?? true,
      hero: this.activeLogo || payload.hero_image?.url,
      genre: payload.genre,
      storeType: payload.store_type,
      phone: payload.location?.phone?.value || '',
      main_icon: payload?.main_icon?.url,
      logo: payload.logo?.url,
      timezone: payload.timezone
    };

    this.themeConfigService.setThemeVariables(payload.theme_styles);

    if (payload?.favicon?.['size-full']) {
      updateFavicon(this.activeFavicon || payload?.favicon?.['size-full']);
    }

    const pickupLocationEntries = Object.entries(
      payload.store?.pickup_locations || {}
    );

    const patchData: Partial<RestaurantStateModel> = {
      general,
      banners: payload.banners,
      workingHours: payload.location?.hours?.value || [],
      holidays: payload.store?.holidays || {}
    };

    const envStores = (environment.happyApi as any)?.stores;
    const hasEnvStores = Array.isArray(envStores) && envStores.length > 0;

    if (!hasEnvStores && pickupLocationEntries.length > 0) {
      patchData.pickupLocations = pickupLocationEntries.reduce(
        (acc: WooCommerceShippingAddress[], [id, pickupLocation]) => {
          acc.push({ ...pickupLocation, id });
          return acc;
        },
        [] as WooCommerceShippingAddress[]
      );
    }

    ctx.patchState(patchData);
  }

  @Action(GetPickupLocationsAction)
  getPickupLocations(
    ctx: StateContext<RestaurantStateModel>
  ): Observable<WooCommerceShippingAddress[]> {
    return this.wooCommerceShippingApiService.getPickupLocations().pipe(
      tap((pickupLocations: WooCommerceShippingAddress[]) => {
        ctx.patchState({ pickupLocations });
      })
    );
  }

  // TODO: Pretty Large task for future. We should change strategy how we load
  //  products, now we get all products - we just send a lot of requests to get
  //  all products once, we should change it to lazy loading! E.G when user enters
  //  to page, he should see only 100 first products (request products by category),
  //  then we should load products consistently (on scroll), when user clicks
  //  on category (in header filters), we should load full category that has been before,
  //  and also load selected category, and few items from next category (if it's
  //  not last).

  @Action(GetRestaurantProductsAction)
  getRestaurantProducts(
    ctx: StateContext<RestaurantStateModel>
  ): Observable<WooCommerceProductsObject> {
    void this.cacheVersionsReconcile.reconcile();

    const loading: RestaurantStateLoading = ctx.getState().loading;
    ctx.patchState({ products: {}, loading: { ...loading, products: true } });

    return this.wooCommerceProductApiService
      .getProducts({ paginate: false })
      .pipe(
        map((products) => mapProducts(products)),
        map((products: WooCommerceProduct[]): WooCommerceProductsObject => {
          return groupProductsByCategory(products);
        }),
        tap((response: WooCommerceProductsObject) => {
          const state: RestaurantStateModel = ctx.getState();
          ctx.patchState({ products: mergeObjects(state.products, response) });
        }),
        finalize(() => {
          const updatedLoading: RestaurantStateLoading = ctx.getState().loading;
          ctx.patchState({ loading: { ...updatedLoading, products: false } });
        })
      );
  }

  @Action(GetRestaurantPaymentMethodsAction)
  getRestaurantPaymentMethods(
    ctx: StateContext<RestaurantStateModel>
  ): Observable<WooCommercePaymentMethod[]> {
    const loading: RestaurantStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, payments: true } });

    return this.wooCommercePaymentApiService.getPaymentMethods().pipe(
      tap((paymentMethods: WooCommercePaymentMethod[]) => {
        ctx.patchState({
          paymentMethods: paymentMethods.map((rawPaymentMethod) => ({
            ...rawPaymentMethod,
            icon: getPaymentIconByTitle(
              rawPaymentMethod.title,
              rawPaymentMethod.gateway
            )
          }))
        });
      }),
      finalize(() => {
        const updatedLoading: RestaurantStateLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, payments: false } });
      })
    );
  }

  @Action(GetRestaurantProductCategoriesAction)
  getRestaurantProductCategories(
    ctx: StateContext<RestaurantStateModel>
  ): Observable<any> {
    const loading: RestaurantStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, categories: true } });

    return this.wooCommerceProductApiService.getProductsCategories().pipe(
      map((rawCategories: WooCommerceProductCategory[]) => {
        const categories: WooCommerceProductCategory[] = rawCategories.filter(
          (category: WooCommerceProductCategory): boolean => {
            return !category.name.toLowerCase().includes('uncategorized');
          }
        );

        return {
          categories: array2Object(categories),
          order: categories.map((c) => c.id)
        };
      }),
      tap(({ categories, order }) => {
        ctx.patchState({
          productCategories: categories,
          productCategoriesOrder: order
        });
      }),
      finalize(() => {
        const updatedLoading: RestaurantStateLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, categories: false } });
      })
    );
  }

  @Action(ClearRestaurantAction)
  clearRestaurant(ctx: StateContext<RestaurantStateModel>) {
    this.themeConfigService.clearThemeVariables();
    ctx.setState(defaults);

    // Re-seed from the per-store `/store/info` cache right after the reset
    // so the logo/name/hours don't disappear while a fresh request is
    // in-flight after a location switch.
    const cached = this.readCachedStoreInfo();
    if (cached) {
      this.applyRestaurantPayload(ctx, cached);
    }
  }

  private readCachedStoreInfo(): WooCommerceRestaurant | null {
    const storeApiKey =
      this.happyStoreSelection.getSelected()?.storeApiKey || null;
    return this.happyStoreInfoCache.get(storeApiKey);
  }
}
